import React from "react";
import { CopyToClipboard, Link, StatusIndicator, TableProps } from "@cloudscape-design/components";
import { FailedExecution } from "../../services/StepFunctionFailuresService";
import { WorkflowStatus } from "../../services";
import { TimestampDisplay } from "../common";
import { ResultFormatter, StatusMapper } from "../../utils";
import { LABELS, SFN_FAILURES_LABELS } from "../../constants";

export const SHORT_ERROR_MAX_LENGTH = 40;

const SINGLE_LINE_STYLE: React.CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

export const shortErrorName = (error?: string): string => {
  if (!error) {
    return LABELS.EMPTY_VALUE;
  }
  if (error.length <= SHORT_ERROR_MAX_LENGTH) {
    return error;
  }
  return error.substring(error.lastIndexOf(".") + 1) || error;
};

function StatusCell(item: FailedExecution): JSX.Element {
  return <StatusIndicator type={StatusMapper.mapWorkflowStatus(item.status as WorkflowStatus)}>{item.status}</StatusIndicator>;
}

function StartedCell(item: FailedExecution): JSX.Element {
  return <TimestampDisplay date={item.startDate} mode="utc" />;
}

function StoppedCell(item: FailedExecution): JSX.Element {
  return <TimestampDisplay date={item.stopDate} mode="utc" />;
}

function ErrorCell(item: FailedExecution): JSX.Element | string {
  if (!item.workflowError?.error) {
    return LABELS.EMPTY_VALUE;
  }
  return (
    <div style={SINGLE_LINE_STYLE} title={item.workflowError.error}>
      {shortErrorName(item.workflowError.error)}
    </div>
  );
}

function CauseCell(item: FailedExecution): JSX.Element | string {
  if (!item.workflowError?.cause) {
    return LABELS.EMPTY_VALUE;
  }
  return <div style={SINGLE_LINE_STYLE}>{item.workflowError.cause}</div>;
}

function CopyCell(item: FailedExecution): JSX.Element {
  return (
    <CopyToClipboard
      variant="icon"
      copyButtonAriaLabel={SFN_FAILURES_LABELS.COPY_ROW_ARIA_LABEL}
      copySuccessText={SFN_FAILURES_LABELS.COPIED}
      copyErrorText={SFN_FAILURES_LABELS.COPY_FAILED}
      textToCopy={ResultFormatter.formatFailedExecution(item)}
    />
  );
}

const buildExecutionCell = (onViewDetails: (item: FailedExecution) => void) => {
  function ExecutionCell(item: FailedExecution): JSX.Element {
    return (
      <div style={SINGLE_LINE_STYLE} title={item.name}>
        <Link fontSize="body-m" onFollow={() => onViewDetails(item)}>
          {item.name}
        </Link>
      </div>
    );
  }
  return ExecutionCell;
};

export const buildFailedExecutionColumnDefinitions = (
  onViewDetails: (item: FailedExecution) => void
): TableProps.ColumnDefinition<FailedExecution>[] => [
  { id: "execution", header: SFN_FAILURES_LABELS.EXECUTION, width: 260, cell: buildExecutionCell(onViewDetails) },
  { id: "status", header: SFN_FAILURES_LABELS.STATUS, width: 120, cell: StatusCell },
  { id: "started", header: SFN_FAILURES_LABELS.STARTED, width: 170, cell: StartedCell },
  { id: "stopped", header: SFN_FAILURES_LABELS.STOPPED, width: 170, cell: StoppedCell },
  { id: "error", header: SFN_FAILURES_LABELS.ERROR, width: 200, cell: ErrorCell },
  { id: "cause", header: SFN_FAILURES_LABELS.CAUSE, cell: CauseCell },
  { id: "copy", header: SFN_FAILURES_LABELS.COPY, width: 80, cell: CopyCell },
];
